import { useTLang } from 'intl/intl';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';

import { featureAdoptionLib } from 'shared/libs/feature-adoption';
import { adoptionAnalytics } from 'shared/libs/feature-adoption/analytics';
import { selectors as partnerSelectors } from '../../../redux/partner';
import { STORE_MANAGER_ADOPTION } from '../domain/mainLayout.const';
import { mainLayoutAdoptionService } from '../services';

import Paths from 'constants/Paths';
import { FeatureAdoptionTypes } from 'components/main-layout/domain/models';

import type { PartnerId } from 'shared/domain';
import type { AdoptionStep } from 'shared/libs/feature-adoption';
import type { AdoptionData } from 'components/main-layout/domain/models';

const useProfile = () => {
  return useSelector(partnerSelectors.selectProfile);
};

const useIsChatV2Available = () => {
  return useSelector(partnerSelectors.selectIsChatV2Available);
};

const useMainLayout = ({ partnerId }: { partnerId: PartnerId }) => {
  const [steps, setSteps] = useState<AdoptionStep[]>([]);
  const { destroyAdoption, startAdoption, tooltip } = featureAdoptionLib({
    delayToStartInMs: 5_000,
  });
  const {
    getStoredData,
    updateStoredData,
    hasDayElapsed,
    addInitialStoredData,
    addStoredData,
    MAX_VIEWS,
    isFastFromOCC,
  } = mainLayoutAdoptionService();
  const { step } = useMainLayoutAdoption({ partnerId, tooltip });
  const isInKanban = !!useMatch(Paths.ORDERS_KANBAN)?.pathname;

  const _prepareStep = (type: FeatureAdoptionTypes) => {
    if (type === FeatureAdoptionTypes.highDemandBtn && isInKanban) {
      return step.highDemandBtn;
    }
    if (
      type === FeatureAdoptionTypes.saturatedZoneHDB &&
      isInKanban &&
      isFastFromOCC
    ) {
      return step.saturatedZone;
    }
  };

  const _prepare = (data: AdoptionData[]) => {
    let items: AdoptionStep[] = [];
    let stepsToUpdate: AdoptionData['type'][] = [];
    const highDemandBtnAdoptionInfo = data.find(
      val => val.type === FeatureAdoptionTypes.highDemandBtn,
    );
    const saturatedZoneHDBAdoptionInfo = data.find(
      val => val.type === FeatureAdoptionTypes.saturatedZoneHDB,
    );

    if (!highDemandBtnAdoptionInfo && isInKanban) {
      items = [...items, step.highDemandBtn];
      addStoredData(FeatureAdoptionTypes.highDemandBtn);
    }
    if (!saturatedZoneHDBAdoptionInfo && isInKanban && isFastFromOCC) {
      items = [...items, step.saturatedZone];
      addStoredData(FeatureAdoptionTypes.saturatedZoneHDB);
    }

    if (isInKanban) {
      stepsToUpdate = [...stepsToUpdate, FeatureAdoptionTypes.highDemandBtn];
    }
    if (isInKanban && isFastFromOCC) {
      stepsToUpdate = [...stepsToUpdate, FeatureAdoptionTypes.saturatedZoneHDB];
    }
    const updatedData = getStoredData();
    updateStoredData(updatedData, stepsToUpdate);

    items = [
      ...items,
      ...updatedData
        .filter(
          value =>
            value.views < MAX_VIEWS && hasDayElapsed(value?.lastShown || ''),
        )
        .map(value => _prepareStep(value.type))
        .filter((item): item is AdoptionStep => item !== undefined),
    ];
    setSteps(items);
  };

  const _setup = () => {
    const steps: AdoptionStep[] = [
      ...(isInKanban ? [step.highDemandBtn] : []),
      ...(isInKanban && isFastFromOCC ? [step.saturatedZone] : []),
    ];
    setSteps(steps);
  };

  useEffect(() => {
    if (!isInKanban || !partnerId) {
      setSteps([]);
      return; // TODO: return here is a bad practice, refactor this
    }
    const data = getStoredData();
    if (data.length > 0) {
      _prepare(data);
    } else {
      addInitialStoredData();
      _setup();
    }
  }, [isInKanban, partnerId, isFastFromOCC]);

  useEffect(() => {
    if (steps.length > 0 && (isInKanban || (isInKanban && isFastFromOCC))) {
      startAdoption(steps);
    }
    return () => {
      destroyAdoption();
    };
  }, [steps, isInKanban, isFastFromOCC]);
};

//TODO: move to another file
export function useMainLayoutAdoption(params) {
  const { partnerId, tooltip } = params;
  const lang = useMainLayoutLang();

  const _highDemandBtnStep: AdoptionStep = {
    element: `#${STORE_MANAGER_ADOPTION}`,
    popover: {
      title: lang.storeManager.title,
      description: lang.storeManager.msg,
      side: 'left',
      align: 'start',
      onNextClick: () => {
        adoptionAnalytics.log({ partnerId, feature: 'gestion_de_demanda' });
        tooltip.current?.moveNext();
      },
    },
  };

  const _saturatedZoneStep: AdoptionStep = {
    element: `#${STORE_MANAGER_ADOPTION}`,
    popover: {
      title: lang.saturatedZone.title,
      description: lang.saturatedZone.msg,
      side: 'left',
      align: 'start',
      onNextClick: () => {
        adoptionAnalytics.log({ partnerId, feature: 'occ_hold_low_ct' });
        tooltip.current?.moveNext();
      },
    },
  };

  return {
    step: {
      highDemandBtn: _highDemandBtnStep,
      saturatedZone: _saturatedZoneStep,
    },
  };
}

//TODO: move to another file
export function useMainLayoutLang() {
  const _storeManagerTitle = useTLang({ id: 'adoption.storeManager' });
  const _storeManagerMsg = useTLang({ id: 'adoption.storeManagerDisclaimer' });
  const _satZoneTitle = useTLang({ id: 'adoption.saturatedZoneHDB.title' });
  const _satZoneMsg = useTLang({ id: 'adoption.saturatedZoneHDB.msg' });
  return {
    storeManager: {
      title: _storeManagerTitle,
      msg: _storeManagerMsg,
    },
    saturatedZone: {
      title: _satZoneTitle,
      msg: _satZoneMsg,
    },
  };
}

const Hooks = { useProfile, useIsChatV2Available, useMainLayout };

export default Hooks;
